import React, { useState } from "react";
import { Button, Dropdown, DropdownList, DropdownListItem, Typography } from "@sumprio/ui";
import { ChevronLeftIcon, ChevronRightIcon } from "@sumprio/ui/icons";

export const LIST_TABLE_PAGE_SIZE_CHOICES = [
  { value: 10, label: "10" },
  { value: 25, label: "25" },
  { value: 50, label: "50" },
  { value: 0, label: "All" },
] as const;

type ListTablePageSizeChoiceOption = { value: number; label: string };

export interface ListTablePaginationProps extends React.HTMLAttributes<HTMLDivElement> {
  page?: number;
  totalPages?: number;
  pageSize?: number;
  totalItems?: number;
  pageSizeTriggerLabel: string;
  selectedPageSizeChoice: number;
  onPageChange?: (_page: number) => void;
  onPageSizeChange?: (_choiceValue: number) => void;
  itemsRangeOverride?: { start: number; end: number };
  hidePageNavigation?: boolean;
  showScrollForMoreHint?: boolean;
  /** Pagination sits under the table, so menus open upward like Cases/Clients. */
  dropdownPlacement?: "auto" | "top-start" | "top-end" | "bottom-start" | "bottom-end";
  pageSizeChoices?: readonly ListTablePageSizeChoiceOption[];
}

export default function ListTablePagination({
  page = 1,
  totalPages = 1,
  pageSize = 10,
  totalItems = 0,
  pageSizeTriggerLabel,
  selectedPageSizeChoice,
  onPageChange,
  onPageSizeChange,
  itemsRangeOverride,
  hidePageNavigation = false,
  showScrollForMoreHint = false,
  dropdownPlacement = "auto",
  pageSizeChoices = LIST_TABLE_PAGE_SIZE_CHOICES,
  className,
  ...props
}: ListTablePaginationProps) {
  const [pageSizeOpen, setPageSizeOpen] = useState(false);
  const [pageOpen, setPageOpen] = useState(false);
  const hasRangeOverride = itemsRangeOverride !== undefined && itemsRangeOverride !== null;
  const startItem = hasRangeOverride
    ? itemsRangeOverride.start
    : totalItems === 0
      ? 0
      : (page - 1) * pageSize + 1;
  const endItem = hasRangeOverride ? itemsRangeOverride.end : Math.min(page * pageSize, totalItems);

  return (
    <div
      className={["table-pagination", className].filter(Boolean).join(" ")}
      role="navigation"
      aria-label="Table pagination"
      {...props}
    >
      <div className="table-pagination__left">
        <span className="table-pagination__label">Results per page:</span>
        <div className="table-pagination__dropdown-wrap">
          <Dropdown
            trigger="inline"
            triggerLabel={pageSizeTriggerLabel}
            placement={dropdownPlacement}
            className="table-pagination__dropdown"
            open={pageSizeOpen}
            onOpenChange={setPageSizeOpen}
          >
            <DropdownList showCTA={false} listMaxHeight="200px">
              {pageSizeChoices.map((choice) => (
                <DropdownListItem
                  key={choice.value}
                  label={choice.label}
                  state={selectedPageSizeChoice === choice.value ? "selected" : "default"}
                  onClick={() => {
                    onPageSizeChange?.(choice.value);
                    setPageSizeOpen(false);
                  }}
                />
              ))}
            </DropdownList>
          </Dropdown>
        </div>
        <Typography
          variant="body"
          bodySize="sm"
          bodyWeight="regular"
          className="table-pagination__range"
        >
          {totalItems === 0 ? "0 items" : `${startItem}-${endItem} of ${totalItems} items`}
        </Typography>
        {showScrollForMoreHint ? (
          <Typography
            variant="body"
            bodySize="sm"
            bodyWeight="regular"
            color="secondary"
            className="table-pagination__scroll-hint"
          >
            Scroll down to load more
          </Typography>
        ) : null}
      </div>
      {!hidePageNavigation ? (
        <div className="table-pagination__right">
          <div className="table-pagination__dropdown-wrap table-pagination__dropdown-wrap--page">
            <Dropdown
              trigger="inline"
              triggerLabel={String(page)}
              placement={dropdownPlacement}
              className="table-pagination__dropdown"
              open={pageOpen}
              onOpenChange={setPageOpen}
            >
              <DropdownList showCTA={false} listMaxHeight="200px">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <DropdownListItem
                    key={p}
                    label={String(p)}
                    state={page === p ? "selected" : "default"}
                    onClick={() => {
                      onPageChange?.(p);
                      setPageOpen(false);
                    }}
                  />
                ))}
              </DropdownList>
            </Dropdown>
          </div>
          <Typography
            variant="body"
            bodySize="sm"
            bodyWeight="regular"
            className="table-pagination__pages"
          >
            of {totalPages} pages
          </Typography>
          <div className="table-pagination__arrows">
            <Button
              type="brand"
              variant="brand-secondaryColor"
              size="xs"
              iconOnly
              icon={ChevronLeftIcon}
              disabled={page <= 1}
              onClick={() => onPageChange?.(page - 1)}
              aria-label="Previous page"
            />
            <Button
              type="brand"
              variant="brand-secondaryColor"
              size="xs"
              iconOnly
              icon={ChevronRightIcon}
              disabled={page >= totalPages}
              onClick={() => onPageChange?.(page + 1)}
              aria-label="Next page"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
